import { loadMseManifest, resolveManifestRelative, type MseClipManifest } from './manifest';
import { fetchPublicAsset } from '../../../utils/publicFetch';
import { fetchMseSegmentArrayBuffer } from './net';
import type { PlayerNetTelemetry } from './net';

type AppendJob = () => Promise<void>;

function lastBufferedEnd(video: HTMLVideoElement): number {
  try {
    const b = video.buffered;
    if (!b || b.length === 0) return 0;
    return b.end(b.length - 1);
  } catch {
    return 0;
  }
}

/**
 * Minimal MSE engine:
 * - One (Managed)MediaSource + one SourceBuffer.
 * - Append init+segments for each clip.
 * - Remove future buffered range (timeline surgery) to branch at a boundary.
 */
export class MseEngine {
  private video: HTMLVideoElement;
  private ms: any = null;
  private sb: SourceBuffer | null = null;
  private queue: AppendJob[] = [];
  private running = false;
  private destroyed = false;
  private netAbort: AbortController | null = (typeof AbortController !== 'undefined') ? new AbortController() : null;

  private mime: string | null = null;
  private impl: string = 'unknown';
  private openTimeoutMs: number = 5000;
  private onNetRecovered: ((stage: 'manifest' | 'segment', meta: any) => void) | null = null;

  constructor(video: HTMLVideoElement, opts?: { onNetRecovered?: (stage: 'manifest' | 'segment', meta: any) => void }) {
    this.video = video;
    this.onNetRecovered = (opts?.onNetRecovered && typeof opts.onNetRecovered === 'function') ? opts.onNetRecovered : null;
  }

  private resetPipeline(opts?: { abortNetwork?: boolean }) {
    this.queue = [];
    this.running = false;
    if (opts?.abortNetwork) {
      try { this.netAbort?.abort(); } catch {}
      this.netAbort = (typeof AbortController !== 'undefined') ? new AbortController() : null;
    }
    try {
      const srcs = new Set<string>();
      try { if (this.video.src) srcs.add(String(this.video.src)); } catch {}
      try { if (this.video.currentSrc) srcs.add(String(this.video.currentSrc)); } catch {}
      for (const src of srcs) {
        if (src && src.startsWith('blob:')) {
          try { URL.revokeObjectURL(src); } catch {}
        }
      }
    } catch {}
    try { this.video.pause(); } catch {}
    try {
      this.video.removeAttribute('src');
      while (this.video.firstChild) this.video.removeChild(this.video.firstChild);
      this.video.load();
    } catch {}
    this.ms = null;
    this.sb = null;
  }

  destroy() {
    this.destroyed = true;
    this.resetPipeline({ abortNetwork: true });
  }

  debugSnapshot(): Record<string, any> {
    return {
      impl: this.impl,
      destroyed: this.destroyed,
      running: this.running,
      queueLength: this.queue.length,
      mime: this.mime || null,
      msReadyState: (() => { try { return this.ms ? String(this.ms.readyState || '') : null; } catch { return null; } })(),
      hasSourceBuffer: Boolean(this.sb),
      bufferedEnd: (() => { try { return this.bufferedEnd(); } catch { return 0; } })(),
    };
  }

  isPipelineOpen(): boolean {
    try {
      return Boolean(!this.destroyed && this.ms && this.sb && String(this.ms.readyState || '') === 'open');
    } catch {
      return false;
    }
  }

  async initWithMime(mime: string, opts?: { openTimeoutMs?: number }) {
    if (this.destroyed) return;
    if (this.ms && this.sb && this.mime === mime && this.isPipelineOpen()) return;
    if (this.ms || this.sb) this.resetPipeline({ abortNetwork: true });
    this.mime = mime;
    this.openTimeoutMs = Math.max(500, Number(opts?.openTimeoutMs ?? 5000));

    // iOS / WKWebView reality:
    // - Some environments expose `ManagedMediaSource` instead of (or in addition to) `MediaSource`.
    // - Older Safari used `WebKitMediaSource`.
    const w = window as any;
    const hasMMS = !!w.ManagedMediaSource;
    const hasMS = !!w.MediaSource;
    const hasWK = !!w.WebKitMediaSource;
    const Ctor = w.ManagedMediaSource || w.MediaSource || w.WebKitMediaSource;
    this.impl = hasMMS ? 'ManagedMediaSource' : hasMS ? 'MediaSource' : hasWK ? 'WebKitMediaSource' : 'unknown';

    if (!Ctor) {
      const e: any = new Error('MSE/MMS not available');
      // Attach structured telemetry for ui_error sink (reportHandledError merges err.telemetry into context).
      // This lets us count how often MSE is truly unavailable without adding a fallback path.
      e.telemetry = {
        stage: 'mse_init',
        mse_impl: this.impl,
        has_mms: hasMMS,
        has_ms: hasMS,
        has_wk: hasWK,
        ua: (typeof navigator !== 'undefined') ? String(navigator.userAgent || '') : '',
      };
      throw e;
    }
    if (typeof Ctor.isTypeSupported === 'function' && !Ctor.isTypeSupported(mime)) {
      throw new Error(`MSE type not supported: ${mime}`);
    }

    const ms = new Ctor();
    this.ms = ms;
    const objUrl = URL.createObjectURL(ms);
    const v = this.video;
    // Follow WebKit MMS guidance: attach as <source> and kick the media pipeline.
    // Setting video.src works in many browsers, but iOS WebKit can be picky.
    try {
      // Ensure autoplay-friendly flags.
      (v as any).playsInline = true;
      v.muted = true;
      (v as any).autoplay = true;
      // Hypothesis: remote playback/AirPlay path can interfere with MMS in some WebViews.
      // Disabling remote playback is harmless for our mini-app and may unblock initialization.
      try { (v as any).disableRemotePlayback = true; } catch {}
      try { v.setAttribute('disableRemotePlayback', ''); } catch {}
    } catch {}
    try {
      // Clear previous sources to avoid weird selection behavior.
      v.removeAttribute('src');
      while (v.firstChild) v.removeChild(v.firstChild);
    } catch {}
    try {
      const s = document.createElement('source');
      s.type = mime;
      s.src = objUrl;
      v.appendChild(s);
    } catch {
      // fallback
      v.src = objUrl;
    }
    try { v.load(); } catch {}
    // On iOS, play() may be the thing that triggers `sourceopen` even if it rejects.
    try {
      const p = v.play();
      // IMPORTANT: play() returns a Promise in modern browsers; try/catch won't catch async rejection.
      if (p && typeof (p as any).catch === 'function') {
        (p as any).catch(() => {});
      }
    } catch {}

    await new Promise<void>((resolve, reject) => {
      let done = false;
      const onOpen = () => {
        if (done) return;
        done = true;
        try { ms.removeEventListener('sourceopen', onOpen); } catch {}
        resolve();
      };
      const onErr = () => {
        if (done) return;
        done = true;
        try { ms.removeEventListener('error', onErr as any); } catch {}
        reject(new Error('MediaSource error'));
      };
      ms.addEventListener('sourceopen', onOpen, { once: true });
      ms.addEventListener('error', onErr as any, { once: true });
      const pollEveryMs = 50;
      const poll = () => {
        if (done) return;
        try {
          if (ms && typeof ms.readyState === 'string' && ms.readyState === 'open') return onOpen();
        } catch {}
        setTimeout(poll, pollEveryMs);
      };
      poll();
      const t = setTimeout(() => {
        if (done) return;
        done = true;
        const msState = (() => { try { return String(ms?.readyState || ''); } catch { return ''; } })();
        reject(new Error(`MediaSource open timeout (ms=${this.openTimeoutMs} impl=${this.impl} msState=${msState})`));
      }, this.openTimeoutMs);
      try { ms.addEventListener('sourceopen', () => clearTimeout(t), { once: true }); } catch {}
    });

    if (this.destroyed) return;
    const sb = ms.addSourceBuffer(mime);
    this.sb = sb;
    // IMPORTANT:
    // - 'sequence' mode keeps an internal append position that DOES NOT rewind when we remove() ranges.
    //   That creates gaps (exactly what we see with Emma: buffer ends at 10.125, but next clip appends at 15.1875).
    // - Use 'segments' mode and set timestampOffset explicitly for deterministic stitching.
    try { (sb as any).mode = 'segments'; } catch {}
  }

  implName(): string {
    return this.impl;
  }

  /**
   * Prefetch manifest + first segment (network-cache warm-up). Does NOT append.
   */
  async prefetchClip(manifestUrl: string): Promise<void> {
    const { manifest, baseUrl } = await loadMseManifest(manifestUrl, {
      onRecovered: (m: any) => { try { this.onNetRecovered?.('manifest', m); } catch {} },
    });
    // fetch init + first segment (best-effort)
    const initUrl = resolveManifestRelative(baseUrl, manifest.init);
    const firstSeg = manifest.segments[0] ? resolveManifestRelative(baseUrl, manifest.segments[0]) : '';
    try { await fetchPublicAsset(initUrl, { cache: 'force-cache' as any }, { feature: 'mse', stage: 'prefetch_init' }); } catch {}
    if (firstSeg) {
      try { await fetchPublicAsset(firstSeg, { cache: 'force-cache' as any }, { feature: 'mse', stage: 'prefetch_seg' }); } catch {}
    }
  }

  /**
   * Remove buffered media from `fromTimeSec` to the end (timeline surgery).
   * Caller should do this well before hitting `fromTimeSec` to avoid stalls.
   */
  async removeFrom(fromTimeSec: number) {
    if (this.destroyed) return;
    const sb = this.sb;
    if (!sb) return;
    const from = Math.max(0, Number(fromTimeSec || 0));
    this.enqueue(async () => {
      const end = lastBufferedEnd(this.video);
      if (end <= from) return;
      await this.sbRemove(from, end);
    });
    await this.drain();
  }

  /**
   * Append a clip (init + all segments) at a specific timeline position.
   * Returns manifest (durations are needed by controller).
   */
  async appendClipAt(
    manifestUrl: string,
    startTimeSec: number,
    opts?: { openTimeoutMs?: number },
  ): Promise<MseClipManifest> {
    if (this.destroyed) throw new Error('destroyed');
    const { manifest, baseUrl } = await loadMseManifest(manifestUrl, {
      onRecovered: (m: any) => { try { this.onNetRecovered?.('manifest', m); } catch {} },
    });
    await this.initWithMime(manifest.mime, opts);
    if (this.destroyed) throw new Error('destroyed');

    const initUrl = resolveManifestRelative(baseUrl, manifest.init);
    const segUrls = manifest.segments.map((s) => resolveManifestRelative(baseUrl, s)).filter(Boolean);

    // Fetch init + all segments in parallel (network is the bottleneck, not SourceBuffer).
    // SourceBuffer.appendBuffer must be sequential, but downloads can overlap.
    const initP = this.fetchArrayBuffer(initUrl);
    const segPs = segUrls.map((u) => this.fetchArrayBuffer(u));
    const initBuf = await initP;

    this.enqueue(async () => {
      if (this.destroyed) return;
      const sb = this.sb;
      if (!sb) {
        if (this.destroyed) return;
        const e: any = new Error('SourceBuffer missing');
        e.telemetry = {
          stage: 'mse_append',
          mse_impl: this.impl,
          mse_destroyed: this.destroyed,
          mse_running: this.running,
          mse_queue_len: this.queue.length,
          ms_state: (() => { try { return String(this.ms?.readyState || ''); } catch { return ''; } })(),
          mime: this.mime || null,
        };
        throw e;
      }
      try { sb.timestampOffset = Math.max(0, Number(startTimeSec || 0)); } catch {}
      await this.sbAppend(initBuf);
      for (let i = 0; i < segPs.length; i++) {
        await this.sbAppend(await segPs[i]);
        await this.evictIfNeeded();
      }
    });
    await this.drain();
    return manifest;
  }

  /**
   * Back-compat: append at current bufferedEnd().
   */
  async appendClip(manifestUrl: string): Promise<MseClipManifest> {
    return await this.appendClipAt(manifestUrl, this.bufferedEnd());
  }

  /**
   * Current end of buffered timeline (seconds).
   */
  bufferedEnd(): number {
    return lastBufferedEnd(this.video);
  }

  private enqueue(job: AppendJob) {
    // Guard: if engine was destroyed while a caller was scheduling work, do not enqueue.
    if (this.destroyed) return;
    // Wrap job to treat destruction as cancellation (do not throw on teardown).
    this.queue.push(async () => {
      if (this.destroyed) return;
      return await job();
    });
  }

  private async drain() {
    if (this.running) return;
    this.running = true;
    try {
      while (!this.destroyed) {
        const job = this.queue.shift();
        if (!job) break;
        try {
          await job();
        } catch (e) {
          // If engine got destroyed mid-flight, treat as cancellation.
          if (this.destroyed) return;
          throw e;
        }
      }
    } finally {
      this.running = false;
    }
  }

  private async fetchArrayBuffer(url: string): Promise<ArrayBuffer> {
    // MSE network policy:
    // - stall-based timeout (not absolute): only abort if no progress for N ms
    // - retries with exponential backoff + jitter
    // - best-effort Range resume between attempts
    const ctrl = (typeof AbortController !== 'undefined') ? new AbortController() : null;
    const onAbort = () => { try { ctrl?.abort(); } catch {} };
    try {
      try { this.netAbort?.signal?.addEventListener?.('abort', onAbort, { once: true } as any); } catch {}
      return await fetchMseSegmentArrayBuffer(url, {
        signal: (ctrl as any)?.signal,
        retry: { attempts: 4, baseDelayMs: 250, backoffFactor: 2, maxDelayMs: 5000, jitterRatio: 0.25 },
        stall: { stallTimeoutMs: 5000, attemptTimeoutMs: 45000 },
        resumeRange: true,
        onRecovered: (m: any) => { try { this.onNetRecovered?.('segment', m); } catch {} },
      });
    } finally {
      try { this.netAbort?.signal?.removeEventListener?.('abort', onAbort as any); } catch {}
    }
  }

  private async sbAppend(buf: ArrayBuffer) {
    const sb = this.sb;
    if (!sb) {
      if (this.destroyed) return;
      const e: any = new Error('SourceBuffer missing');
      e.telemetry = {
        stage: 'mse_sb_append',
        mse_impl: this.impl,
        mse_destroyed: this.destroyed,
        mse_running: this.running,
        mse_queue_len: this.queue.length,
        ms_state: (() => { try { return String(this.ms?.readyState || ''); } catch { return ''; } })(),
        mime: this.mime || null,
      };
      throw e;
    }
    await this.sbWaitIdle();
    try {
      sb.appendBuffer(buf);
    } catch (e: any) {
      // QuotaExceededError: try eviction and retry once
      await this.evictAggressive();
      await this.sbWaitIdle();
      sb.appendBuffer(buf);
    }
    await this.sbWaitUpdateEnd();
  }

  private async sbRemove(from: number, to: number) {
    const sb = this.sb;
    if (!sb) return;
    await this.sbWaitIdle();
    try { sb.remove(from, to); } catch { return; }
    await this.sbWaitUpdateEnd();
  }

  private async sbWaitIdle() {
    const sb = this.sb;
    if (!sb) return;
    if (!sb.updating) return;
    await this.sbWaitUpdateEnd();
  }

  private async sbWaitUpdateEnd() {
    const sb = this.sb;
    if (!sb) return;
    await new Promise<void>((resolve) => {
      const finish = () => resolve();
      try { sb.addEventListener('updateend', finish, { once: true }); } catch { resolve(); }
      setTimeout(finish, 8000);
    });
  }

  private async evictIfNeeded() {
    // Keep only ~12s behind currentTime to reduce memory pressure (best-effort)
    const keepBehind = 12;
    const t = Number(this.video.currentTime || 0);
    const removeTo = Math.max(0, t - keepBehind);
    if (removeTo <= 0.5) return;
    await this.sbRemove(0, removeTo);
  }

  private async evictAggressive() {
    // Hard reset of buffered history (best-effort) to recover from QuotaExceededError.
    const end = lastBufferedEnd(this.video);
    const t = Number(this.video.currentTime || 0);
    const from = Math.max(0, t - 1);
    if (end > from) {
      await this.sbRemove(0, from);
    }
  }
}

